import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Avatar,
  Divider,
} from '@mui/material';
import Footer from '../components/Footer';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DevicesIcon from '@mui/icons-material/Devices';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const AboutPage = () => {
  // Founder data
  const founder = {
    name: 'Devang Sharma',
    position: 'Founder & CEO',
    image: '/assets/images/devang-sharma.png',
    bio: 'Despite his young age, Devang brings extraordinary vision and innovation to QResto. With a background in design and marketing, he identified the gap between traditional dining and emerging tech, creating QResto to revolutionize the restaurant experience.',
  };

  // Company values
  const values = [
    {
      title: 'Innovation',
      description: `We constantly push the boundaries of what's possible in restaurant technology.`,
      icon: <QrCodeIcon fontSize="large" />,
    },
    {
      title: 'Quality',
      description: `We're committed to delivering high-quality digital experiences that delight users.`,
      icon: <RestaurantMenuIcon fontSize="large" />,
    },
    {
      title: 'Accessibility',
      description: 'We believe advanced technology should be accessible to restaurants of all sizes.',
      icon: <DevicesIcon fontSize="large" />,
    },
    {
      title: 'Customer Success',
      description: `Our partners' success is our success – we're dedicated to exceptional support.`,
      icon: <SupportAgentIcon fontSize="large" />,
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 8, sm: 12, md: 16 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h1"
              component="h1"
              gutterBottom
              sx={{
                fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
                fontWeight: 800,
                mb: 3,
              }}
            >
              About QResto
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 4, 
                opacity: 0.9, 
                maxWidth: '800px', 
                mx: 'auto',
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
              }}
            >
              We're transforming restaurant menus with digital technology, one QR code at a time
            </Typography>
          </Box>
        </Container>
        
        {/* Visual elements in the background */}
        <Box sx={{ 
          position: 'absolute', 
          top: -100, 
          left: -100, 
          width: 300, 
          height: 300, 
          bgcolor: 'rgba(255,255,255,0.05)',
          borderRadius: '50%' 
        }} />
        <Box sx={{ 
          position: 'absolute', 
          bottom: -80, 
          right: -80, 
          width: 250, 
          height: 250, 
          bgcolor: 'rgba(255,255,255,0.08)',
          borderRadius: '50%' 
        }} />
      </Box>
      
      {/* Our Story Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8, md: 12 } }}>
        <Grid container spacing={{ xs: 4, sm: 5, md: 6 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography 
              variant="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              Our Story
            </Typography>
            <Typography 
              variant="body1" 
              paragraph 
              sx={{ 
                fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' }
              }}
            >
              Founded in 2023, QResto was born from a simple observation: restaurant menus hadn't evolved in decades, despite all the technological advancements around us.
            </Typography>
            <Typography 
              variant="body1" 
              paragraph 
              sx={{ 
                fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' }
              }}
            >
              Our founder, Devang Sharma, a young innovator with exceptional design sensibilities and marketing expertise, was dining at a restaurant when he realized the gap between the traditional paper menu and the immersive digital experiences we've come to expect in other aspects of life.
            </Typography>
            <Typography 
              variant="body1" 
              paragraph 
              sx={{ 
                fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' }
              }}
            >
              Despite his youth, Devang's forward-thinking approach and creative problem-solving led to the birth of QResto — an elegant solution that brings menus to life through digital technology, accessible via a simple QR code scan, without requiring any app downloads.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' }
              }}
            >
              Today, we're proud to partner with restaurants across India, helping them create memorable dining experiences through technology that's both cutting-edge and accessible, all guided by Devang's innovative vision and deep understanding of user experience design.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80"
              alt="Restaurant table with QR code"
              sx={{
                width: '100%',
                borderRadius: 4,
                boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
              }}
            />
          </Grid>
        </Grid>
      </Container>
      
      {/* Our Values Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, sm: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, sm: 8 } }}>
            <Typography variant="overline" color="primary" fontWeight="bold">
              WHAT DRIVES US
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              Our Values
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '700px', 
                mx: 'auto',
                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
              }}
            >
              These core principles guide everything we do at QResto
            </Typography>
          </Box>
          
          <Grid container spacing={{ xs: 3, sm: 4 }}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, sm: 4 },
                    height: '100%',
                    borderRadius: 4,
                    bgcolor: 'background.paper',
                    textAlign: 'center',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {value.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight="medium">
                    {value.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {value.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* Meet Our Founder Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 6, sm: 8 } }}>
          <Typography variant="overline" color="primary" fontWeight="bold">
            LEADERSHIP
          </Typography>
          <Typography 
            variant="h2" 
            sx={{ 
              mb: 2, 
              fontWeight: 700,
              fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
            }}
          >
            Meet Our Founder
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              maxWidth: '700px', 
              mx: 'auto',
              fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
            }}
          >
            The visionary behind QResto's innovative dining experience
          </Typography>
        </Box>
        
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={8} md={6} lg={4}>
            <Card sx={{ 
              borderRadius: 4,
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
              },
            }}>
              <CardContent sx={{ textAlign: 'center', p: { xs: 3, sm: 4 } }}>
                <Avatar
                  src={founder.image}
                  alt={founder.name}
                  sx={{ 
                    width: { xs: 140, sm: 180 }, 
                    height: { xs: 140, sm: 180 }, 
                    mx: 'auto', 
                    mb: 3,
                    border: '4px solid white',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                  }}
                />
                <Typography variant="h4" component="h3" gutterBottom fontWeight="bold">
                  {founder.name}
                </Typography>
                <Typography 
                  variant="h6" 
                  color="primary" 
                  gutterBottom
                  sx={{ fontWeight: 500, mb: 3 }}
                >
                  {founder.position}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {founder.bio}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Team Description */}
        <Box sx={{ textAlign: 'center', mt: { xs: 6, sm: 8 }, maxWidth: '800px', mx: 'auto' }}>
          <Typography 
            variant="h5" 
            gutterBottom 
            fontWeight="bold" 
            sx={{ 
              mb: 3,
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}
          >
            Supported by a Talented Team
          </Typography>
          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' }, 
              lineHeight: 1.8 
            }}
          >
            Behind QResto's success is a dedicated team of talented developers, creative designers, 
            marketing experts, and customer success specialists. Together, we work tirelessly to 
            bring innovation to the restaurant industry and deliver exceptional experiences to our partners.
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' }, 
              lineHeight: 1.8 
            }}
          >
            Each team member brings unique expertise and passion, united by a common goal: 
            revolutionizing the way diners experience restaurant menus through cutting-edge digital technology.
          </Typography>
        </Box>
      </Container>
      
      {/* Our Mission Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 6, sm: 8, md: 10 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography 
            variant="h3" 
            gutterBottom 
            fontWeight="bold"
            sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}
          >
            Our Mission
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto', 
              lineHeight: 1.6, 
              opacity: 0.9,
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
            }}
          >  To revolutionize the dining experience by bridging the gap between traditional menus and digital innovation, making modern technology accessible to restaurants of all sizes across India.
          </Typography>
        </Container>
      </Box>
      
      <Footer />
    </>
  );
};

export default AboutPage;